import { assetUrl } from '@/utils/assetUrl'

export interface ModalResult {
  confirm: boolean
  cancel: boolean
  content?: string
}

export function showToast(options: { title: string; icon?: string }) {
  const el = document.createElement('div')
  el.className = 'pindou-toast'
  el.textContent = options.title
  document.body.appendChild(el)
  requestAnimationFrame(() => el.classList.add('pindou-toast--show'))
  setTimeout(() => {
    el.classList.remove('pindou-toast--show')
    setTimeout(() => el.remove(), 200)
  }, 1800)
}

export function showModal(options: {
  title?: string
  content?: string
  editable?: boolean
  placeholderText?: string
  showCancel?: boolean
  confirmText?: string
  cancelText?: string
  success?: (res: ModalResult) => void
}) {
  if (options.editable) {
    const defaultValue = options.content ?? ''
    const label = [options.title, options.placeholderText].filter(Boolean).join('\n')
    const input = window.prompt(label || '请输入', defaultValue)
    if (input === null) {
      options.success?.({ confirm: false, cancel: true })
    } else {
      options.success?.({ confirm: true, cancel: false, content: input })
    }
    return
  }

  const message = [options.title, options.content].filter(Boolean).join('\n')
  if (options.showCancel === false) {
    window.alert(message)
    options.success?.({ confirm: true, cancel: false })
    return
  }
  const ok = window.confirm(message)
  options.success?.({ confirm: ok, cancel: !ok })
}

export function showActionSheet(options: {
  itemList: string[]
  success?: (res: { tapIndex: number }) => void
  fail?: () => void
}) {
  const existing = document.querySelector('.pindou-action-sheet')
  existing?.remove()

  const overlay = document.createElement('div')
  overlay.className = 'pindou-action-sheet'
  overlay.setAttribute('role', 'dialog')
  overlay.setAttribute('aria-modal', 'true')

  const panel = document.createElement('div')
  panel.className = 'pindou-action-sheet__panel'

  const list = document.createElement('div')
  list.className = 'pindou-action-sheet__list'

  const close = (index: number | null) => {
    overlay.remove()
    document.removeEventListener('keydown', onKey)
    if (index === null) options.fail?.()
    else options.success?.({ tapIndex: index })
  }

  const onKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') close(null)
  }

  options.itemList.forEach((label, index) => {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'pindou-action-sheet__item'
    button.textContent = label
    button.addEventListener('click', () => close(index))
    list.appendChild(button)
  })

  const cancel = document.createElement('button')
  cancel.type = 'button'
  cancel.className = 'pindou-action-sheet__cancel'
  cancel.textContent = '取消'
  cancel.addEventListener('click', () => close(null))

  panel.appendChild(list)
  panel.appendChild(cancel)
  overlay.appendChild(panel)
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) close(null)
  })
  document.addEventListener('keydown', onKey)
  document.body.appendChild(overlay)
  requestAnimationFrame(() => overlay.classList.add('pindou-action-sheet--show'))
}

function copyWithTextarea(text: string): boolean {
  const ta = document.createElement('textarea')
  ta.value = text
  ta.setAttribute('readonly', '')
  ta.style.position = 'fixed'
  ta.style.left = '-9999px'
  document.body.appendChild(ta)
  ta.select()
  let ok = false
  try {
    ok = document.execCommand('copy')
  } catch {
    ok = false
  }
  ta.remove()
  return ok
}

export function setClipboardData(options: {
  data: string
  success?: () => void
  fail?: () => void
}) {
  const done = (ok: boolean) => {
    if (ok) options.success?.()
    else options.fail?.()
  }

  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(options.data).then(() => done(true)).catch(() => {
      done(copyWithTextarea(options.data))
    })
    return
  }
  done(copyWithTextarea(options.data))
}

export function getSystemInfoSync() {
  return {
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    pixelRatio: window.devicePixelRatio,
  }
}

export function scanCode(_options: { success?: (res: { result: string }) => void }) {
  showToast({ title: '网页端请粘贴分享码', icon: 'none' })
}

export function chooseMessageFile(_options: unknown) {
  showToast({ title: '网页端请使用文件导入', icon: 'none' })
}

export function getFileSystemManager() {
  return {
    readFile: (_opts: { fail?: () => void }) => _opts.fail?.(),
  }
}

export function request(options: {
  url: string
  success?: (res: { data: unknown }) => void
  fail?: () => void
}) {
  fetch(assetUrl(options.url))
    .then((r) => r.json())
    .then((data) => options.success?.({ data }))
    .catch(() => options.fail?.())
}
